import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { concatMap, Subscription } from 'rxjs';
import { tap, finalize, map } from 'rxjs/operators';

import { MessageService } from 'primeng/api';
import { DropdownModule } from 'primeng/dropdown';
import { KeycloakService } from 'keycloak-angular';

import { environment } from '../../environments/environment';

import { GlobalConnSettings, GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from '../services/getglobalsettings.service'
import { CliListEntry } from '../shared/cli-list-entry';
import { RestApiService } from '../services/rest-api.service';
import { SecureApiService } from '../services/secure-api.service';
import { CurrencyRate } from '../shared/currency'

@Component({
  selector: 'app-setup',
  templateUrl: './setup.component.html',
  styleUrls: ['./setup.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Application setup and user-preference page.
 *
 * Loads settings via `getObservableGlobalSettings` → `concatMap(getObservableLocalConnSettings)`
 * with a self-unsubscribing `finalize`. Settings are edited on a working copy (`edited` /
 * `editconn`) and committed on `onConfirm`: case 5 saves connection settings via
 * `setConnSettings`; cases 1–4 persist user settings to localStorage via `setGlobalSettings`
 * and POST them to the server via `postManagerSettings`.
 *
 * `mainCompanySelected` cascades the company change by resetting the responsible person and
 * all stock/department IDs to `null`. `isAdminRole` gates admin-only UI sections via the
 * Keycloak `portal-admin` role.
 */
export class SetupComponent implements OnInit, OnDestroy {

  public userAccessGroup: number;

  settings: GlobalSettingsData = null;
  edited: GlobalSettingsData = null;

  connection: GlobalConnSettings = null;
  editconn: GlobalConnSettings = null;

  private securityQuery: Subscription;

  /* main Company selection */

  clientslistMC: CliListEntry[] = [];

  clientMC: CliListEntry = null;

  selectedCompany: string = '';

  newCurrency: CurrencyRate = { CURID: 0, CURRRATEDATE: null, CURRRATEVALUE: 0 };

  totalRecordsMC: number = 0;

  displayNC: boolean = false;

  apiServers: string[] = [];
  dbServers: string[] = [];
  authServers: {url: string; clientId: string;}[] = [];
  reportServers: string[] = [];

  @ViewChild('mainCompanyDd') mainCompany: DropdownModule;

  /** `true` when the authenticated user has the `portal-admin` Keycloak role. */
  public get isAdminRole(): boolean { return this.auth.isUserInRole('portal-admin'); }

  constructor(
    private restApi: RestApiService,
    public auth: KeycloakService,
    private secApi: SecureApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) {
  }

  /** Unsubscribes the settings-save query. */
  ngOnDestroy(): void {
    console.log('*** SetupComponent ngOnDestroy: ')

    if (this.securityQuery) { this.securityQuery.unsubscribe(); }
  }

  /** Loads global and connection settings into working copies and resolves server-list arrays. */
  ngOnInit(): void {
    this.titleService.setTitle('Глобальные настройки - подключение и параметры пользователя');

    /*
     * NOTE: getGlobalSettings() alwais is loading settings from local storage.
     * 
     * Settings stored in local storage and
     * put there during the initial authentification in toolbar component
     *
     * For now we load it and create the copy for edit purposes
     * On the end we save it and save it in local storage too...
     * 
     */
    const globalSettingsSub = this.globalsettingsservice.getObservableGlobalSettings().pipe(
      //tap(a => environment.production || console.log('*** SetupComponent onInit globalSettingsSub: ' + JSON.stringify(a))),
      map(config => {
        this.settings = config;
        return config;
      }),

      concatMap(() => this.globalsettingsservice.getObservableLocalConnSettings().pipe(
        tap(conn => environment.production || console.log('*** SetupComponent onInit globalSettingsSub (conn): ' + JSON.stringify(conn))),

        map(conn => {
          this.connection = conn;

          if ("apiServer" in this.connection) {
            this.connection.apiSrvAddress = this.connection.apiServer.url;
            this.apiServers = [... this.connection.apiServer.items];
          }

          if ("dbServer" in this.connection) {
            this.connection.dbSrvAddress = this.connection.dbServer.url;
            this.dbServers = [... this.connection.dbServer.items];

            // This temporary assignments are needed for backward compatibility
            // In general we have to disable changing the database name and user login from UI

            this.connection.dataBaseName = this.connection.dbServer.dbName;
            this.connection.dbUserLogin = this.connection.dbServer.userLogin;
          }

          if ("authServer" in this.connection) {
            this.connection.authSrvAddress = this.connection.authServer.url;
            this.authServers = [... this.connection.authServer.items];
          }

          if ("reportServer" in this.connection) {
            this.connection.reportSrvAddress = this.connection.reportServer.url1;
            this.reportServers = [... this.connection.reportServer.items1];
          }

          this.editconn = { ... this.connection };
          this.edited = Object.assign(new GlobalSettingsData(), this.settings);

          this.userAccessGroup = this.globalsettingsservice.getAccessGroup();
        }),
      )),

      finalize(() => {
        globalSettingsSub.unsubscribe();
      })
    ).subscribe({
      next: () => {
      },
      error: err => {
        console.error('*** SetupComponent in globalSettingsSub: Error: ' + err);
      },
      complete: () => {
        environment.production || console.log('*** SetupComponent in globalSettingsSub: Completed: ' + JSON.stringify(this.edited));
      }
    });

//    if (!environment.production) {
//      for (let propName in this.edited) {
//        environment.production || console.log('*** SetupComponent onInit (' + propName + ' )=' + this.edited[propName]);
//      }
//    }
//    this.connection = { ... this.globalsettingsservice.getConnSettings() };

//    if ("apiServer" in this.connection) {
//      this.connection.apiSrvAddress = this.connection.apiServer.url;
//      this.apiServers = [... this.connection.apiServer.items];
//    }

//    if ("dbServer" in this.connection) {
//      this.connection.dbSrvAddress = this.connection.dbServer.url;
//      this.dbServers = [... this.connection.dbServer.items];
//    }

//    if ("authServer" in this.connection) {
//      this.connection.authSrvAddress = this.connection.authServer.url;
//      this.authServers = [... this.connection.authServer.items];
//    }

//    if ("reportServer" in this.connection) {
//      this.connection.reportSrvAddress = this.connection.reportServer.url1;
//      this.reportServers = [... this.connection.reportServer.items1];
//    }

//    this.editconn = { ... this.connection };

//    this.edited = Object.assign(new GlobalSettingsData(), this.settings);

////     this.edited = { ... this.settings };

//    environment.production || console.log('*** SetupComponent onInit (editconn): ' + JSON.stringify(this.editconn));
  }

  /** Logs the opened accordion tab index (diagnostic only). */
  onTabOpen(e) {
    var index = e.index;
    console.log('*** SetupComponent onTabOpen: ' + index);
  }

  /** Applies a new main company and resets person, stocks, and departments to `null`. */
  mainCompanySelected(companyId) {
    console.log('*** SetupComponent mainCompanySelected: ' + companyId)

    if (this.edited.mainCompanyId != companyId) {

      this.edited.mainCompanyId = companyId;
      this.edited.defaultTradeCompanyId = companyId;  // Same company by default

      /*
       * TODO: possiblesolution - load first, available person
       * OR need function alike getDefaultPerson(companyId) => ???
       */
      this.edited.mainPersonId = null;                // Reset

      // reset All department to null (C++ trick !!!)

      this.edited.mainStockId =
        this.edited.impStockId =
        this.edited.auxStockId =
        this.edited.paidDepartmentId =
        this.edited.cashDepartmentId =
        this.edited.manDepartmentId =
        this.edited.auxDepartmentId = null;
    }
  }

  /** Applies the selected PCO (trade) company ID to `edited`. */
  pcoCompanySelected(companyId) {
    this.edited.defaultTradeCompanyId = companyId;
    console.log('*** SetupComponent mainCompanySelected: ' + companyId)
  }

  /** Applies the selected responsible person ID to `edited`. */
  mainPersonSelected(personId) {
    this.edited.mainPersonId = personId;
    console.log('*** SetupComponent mainPersonSelected: ' + personId)
  }

  /** Applies the selected main currency to `edited.mainCurrencyId`. */
  getSelectedMCurrency(currencyId) {
    console.log('*** SetupComponent getSelectedCurrency: ' + JSON.stringify(currencyId));

    if (currencyId && 'CURID' in currencyId) {
      this.edited.mainCurrencyId = currencyId.CURID;
    }
  }

  /** Applies the selected balance currency to `edited.balanceCurrencyId`. */
  getSelectedBCurrency(currencyId) {
    console.log('*** SetupComponent getSelectedCurrency: ' + JSON.stringify(currencyId));

    if (currencyId && 'CURID' in currencyId) {
      this.edited.balanceCurrencyId = currencyId.CURID;
    }
  }

  /** Applies the selected display currency D to `edited.viewDCurrencyId`. */
  getSelectedDCurrency(currencyId) {
    console.log('*** SetupComponent getSelectedDCurrency: ' + JSON.stringify(currencyId));

    if (currencyId && 'CURID' in currencyId) {
      this.edited.viewDCurrencyId = currencyId.CURID;
    }
  }

  /** Applies the selected display currency E to `edited.viewECurrencyId`. */
  getSelectedECurrency(currencyId) {
    console.log('*** SetupComponent getSelectedECurrency: ' + JSON.stringify(currencyId));

    if (currencyId && 'CURID' in currencyId) {
      this.edited.viewECurrencyId = currencyId.CURID;
    }
  }

  /** Applies the selected main stock ID to `edited`. */
  mainStockSelected(stockId) {
    this.edited.mainStockId = stockId;
    console.log('*** SetupComponent mainStockSelected: ' + stockId)
  }

  /** Applies the selected import stock ID to `edited`. */
  impStockSelected(stockId) {
    this.edited.impStockId = stockId;
    console.log('*** SetupComponent impStockSelected: ' + stockId)
  }

  /** Applies the selected auxiliary stock ID to `edited`. */
  auxStockSelected(stockId) {
    this.edited.auxStockId = stockId;
    console.log('*** SetupComponent auxStockSelected: ' + stockId)
  }

  /** Applies the selected paid-orders department ID to `edited`. */
  paidStockSelected(stockId) {
    this.edited.paidDepartmentId = stockId;
    console.log('*** SetupComponent paidStockSelected: ' + stockId)
  }

  /** Applies the selected cash department ID to `edited`. */
  cashStockSelected(stockId) {
    this.edited.cashDepartmentId = stockId;
    console.log('*** SetupComponent cashStockSelected: ' + stockId)
  }

  /** Applies the selected management department ID to `edited`. */
  manStockSelected(stockId) {
    this.edited.manDepartmentId = stockId;
    console.log('*** SetupComponent manStockSelected: ' + stockId)
  }

  /** Applies the selected auxiliary department ID to `edited`. */
  varStockSelected(stockId) {
    this.edited.auxDepartmentId = stockId;
    console.log('*** SetupComponent varStockSelected: ' + stockId)
  }

  /** Saves connection (case 5) or user settings (cases 1–4) and POSTs them to the server. */
  onConfirm(e: number) {
    console.log('*** SetupComponent onConfirm: ' + e)

    switch (e) {
      case 5:
        environment.production || console.log('*** SetupComponent onConfirmCurr: (Connection 1) ' + JSON.stringify(this.editconn.apiSrvAddress));
        environment.production || console.log('*** SetupComponent onConfirmCurr: (Connection 2) ' + JSON.stringify(this.editconn.dbSrvAddress));
        environment.production || console.log('*** SetupComponent onConfirmCurr: (Connection 3) ' + JSON.stringify(this.editconn.dataBaseName));
        environment.production || console.log('*** SetupComponent onConfirmCurr: (Connection 4) ' + JSON.stringify(this.editconn.dbUserLogin));
        environment.production || console.log('*** SetupComponent onConfirmCurr: (Connection 5) ' + JSON.stringify(this.editconn.reportSrvAddress));

        this.connection = { ... this.editconn };
        environment.production || console.log('*** SetupComponent onConfirm (connection): ' + JSON.stringify(this.connection));

        this.globalsettingsservice.setConnSettings(this.connection);
        break;

      case 1:
      case 2:
      case 3:
      case 4:

        environment.production || console.log('*** SetupComponent onConfirm (edited): ' + JSON.stringify(this.edited));

        // this.settings = { ... this.edited };

        this.settings = Object.assign(new GlobalSettingsData(), this.edited);

        environment.production || console.log('*** SetupComponent onConfirm (settings): ' + JSON.stringify(this.settings));

        this.globalsettingsservice.setGlobalSettings(this.settings);

        this.securityQuery =
          this.secApi.postManagerSettings(this.settings)
            .pipe(
              finalize(() => {
                environment.production || console.log('*** SetupComponent on data saving: ' + JSON.stringify(this.settings));
              }))
            .subscribe();
        break;

      default:
        break;
    }
  }

  /** Placeholder for access-group management (diagnostic only). */
  onAccessGroup() {
    console.log('*** SetupComponent onAccessGroup')
  }

  /** Placeholder for new-company creation (diagnostic only). */
  onNewCompany() {
    console.log('*** SetupComponent onNewCompany')
  }

  /** Opens the new-currency dialog with a blank rate entry. */
  onNewCurrency() {
    console.log('*** SetupComponent onNewCurrency')
    this.newCurrency.CURID = null;
    this.newCurrency.CURRRATEDATE = new Date();
    this.newCurrency.CURRRATEVALUE = 0.0;

    this.displayNC = true;
  }

  /** Closes the new-currency dialog. */
  onCurrencyDialogClose(event) {
    this.displayNC = event;
  }

  /** Reverts `edited` to the last saved `settings` snapshot. */
  onReset() {
    console.log('*** SetupComponent onReset')
    this.edited = this.settings;
  }

  /** Logs the main-company click event (diagnostic only). */
  onClickMainCompany(e) {
    console.log('*** SetupComponent onClickMainCompany: ' + e)
  }

  /** Applies the dropdown-selected company to `edited.mainCompanyId` and shows a toast. */
  onChangeMainCompany(e) {
    console.log('*** SetupComponent onChangeMainCompany: ' + e)

    this.clientMC = e.value;

    console.log('*** SetupComponent onChangeMainCompany: selectedCompany: ' + this.clientMC.CLIENTSNIKNAME)
    this.messageService.add({ severity: 'success', summary: 'Selected company: ', detail: this.clientMC.CLIENTSNIKNAME });

    this.edited.mainCompanyId = Number(this.clientMC.CLIID);
  }

  /** Applies the newly selected currency to `edited.mainCurrencyId`. */
  onNewCurrencySelect(e) {
    console.log('*** SetupComponent onNewCurrencySelect: newCurrency: ' + this.newCurrency.CURID)
    this.newCurrency = e;
    this.edited.mainCurrencyId = this.newCurrency.CURID;
  }
}
